import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { motion } from 'framer-motion';
import { alaSpring } from './alaMotion';

const image1Def: SlotGroupDefinition = {
  label: 'Gallery Image 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Community clubhouse lounge',
    },
  },
};

const image2Def: SlotGroupDefinition = {
  label: 'Gallery Image 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Apartment community exterior',
    },
  },
};

const image3Def: SlotGroupDefinition = {
  label: 'Gallery Image 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Resort-style pool amenity',
    },
  },
};

const image4Def: SlotGroupDefinition = {
  label: 'Gallery Image 4',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Furnished apartment interior',
    },
  },
};

export function HomeGallery() {
  const image1 = useSlotGroup('homegallery.image_1', image1Def);
  const image2 = useSlotGroup('homegallery.image_2', image2Def);
  const image3 = useSlotGroup('homegallery.image_3', image3Def);
  const image4 = useSlotGroup('homegallery.image_4', image4Def);

  return (
    <section className="flex w-full flex-col items-center overflow-clip bg-[rgb(var(--ala-surface))] pb-0 pt-8 md:pt-12">
      <div className="flex w-full max-w-[1440px] flex-col items-center gap-12 px-4 md:px-8">
        <div className="flex h-auto w-full flex-col items-center justify-center gap-2 md:mt-24 md:h-[750px] md:flex-row md:items-start md:gap-4">
          <EditableSlotGroup groupPath="homegallery.image_1" definition={image1Def}>
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: '-10% 0px' }}
              transition={{ ...alaSpring, delay: 0 }}
              className="h-[300px] w-full overflow-clip rounded-[4px] md:h-[380px] md:w-[332px] md:shrink-0"
            >
              <VibesitesImage
                url={
                  image1.image ||
                  ''
                }
                alt={image1.alt || ''}
                sizes="(min-width: 768px) 332px, 100vw"
                className="h-full w-full object-cover transition duration-700 hover:scale-105"
              />
            </motion.div>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="homegallery.image_2" definition={image2Def}>
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: '-10% 0px' }}
              transition={{ ...alaSpring, delay: 0.4 }}
              className="h-[300px] w-full overflow-clip rounded-[4px] md:h-[480px] md:w-[332px] md:shrink-0"
            >
              <VibesitesImage
                url={
                  image2.image ||
                  ''
                }
                alt={image2.alt || ''}
                sizes="(min-width: 768px) 332px, 100vw"
                className="h-full w-full object-cover transition duration-700 hover:scale-105"
              />
            </motion.div>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="homegallery.image_3" definition={image3Def}>
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: '-10% 0px' }}
              transition={{ ...alaSpring, delay: 0.8 }}
              className="h-[300px] w-full overflow-clip rounded-[4px] md:h-[430px] md:w-[332px] md:shrink-0"
            >
              <VibesitesImage
                url={
                  image3.image ||
                  ''
                }
                alt={image3.alt || ''}
                sizes="(min-width: 768px) 332px, 100vw"
                className="h-full w-full object-cover transition duration-700 hover:scale-105"
              />
            </motion.div>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="homegallery.image_4" definition={image4Def}>
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: '-10% 0px' }}
              transition={{ ...alaSpring, delay: 1.2 }}
              className="h-[300px] w-full overflow-clip rounded-[4px] md:h-[360px] md:w-[332px] md:shrink-0"
            >
              <VibesitesImage
                url={
                  image4.image ||
                  ''
                }
                alt={image4.alt || ''}
                sizes="(min-width: 768px) 332px, 100vw"
                className="h-full w-full object-cover transition duration-700 hover:scale-105"
              />
            </motion.div>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeGallery',
  displayName: 'Home Gallery',
  tokens: {},
  slotGroups: {
    'homegallery.image_1': image1Def,
    'homegallery.image_2': image2Def,
    'homegallery.image_3': image3Def,
    'homegallery.image_4': image4Def,
  },
};

(HomeGallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HomeGallery;
